import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../UI/card';
import { Button } from '../UI/button';
import { Badge } from '../UI/badge';
import {
  FileText,
  Calendar,
  DollarSign,
  Users,
  Building,
  Download,
  Mail,
  X,
  CheckCircle,
  Clock,
  Send
} from 'lucide-react';

/**
 * COMPOSANT: DÉTAIL D'UN APPEL À COTISATION
 * 
 * Affiche les informations détaillées d'un appel
 */

const CallDetail = ({ call, onClose }) => {
  if (!call) return null;

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { label: 'Brouillon', className: 'bg-gray-100 text-gray-800' },
      SENT: { label: 'Envoyé', className: 'bg-blue-100 text-blue-800' },
      RECEIVED: { label: 'Reçu', className: 'bg-green-100 text-green-800' },
      FAILED: { label: 'Échec', className: 'bg-red-100 text-red-800' }
    };
    return badges[status] || badges.DRAFT;
  };

  const statusBadge = getStatusBadge(call.status);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF'
    }).format(amount);
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const handleDownload = async (type) => {
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contribution-calls/${call.id}/download/${type}`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Erreur téléchargement');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `appel-cotisation-${call.month}-${call.year}.${type}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erreur téléchargement:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Appel à Cotisation
              </h2>
              <p className="text-sm text-gray-600">
                {monthNames[call.month - 1]} {call.year}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-6">
          {/* Statut et informations principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Statut</p>
                    <Badge className={statusBadge.className}>
                      {call.status === 'DRAFT' && <Clock className="w-3 h-3 mr-1" />}
                      {call.status === 'SENT' && <Send className="w-3 h-3 mr-1" />}
                      {call.status === 'RECEIVED' && <CheckCircle className="w-3 h-3 mr-1" />}
                      {statusBadge.label}
                    </Badge>
                  </div>
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Période</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {monthNames[call.month - 1]} {call.year}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Statistiques */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Statistiques</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Montant Total</p>
                    <p className="text-xl font-bold text-green-700">
                      {formatCurrency(call.totalAmount)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Employés</p>
                    <p className="text-xl font-bold text-blue-700">
                      {call.totalEmployees}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Polices</p>
                    <p className="text-xl font-bold text-purple-700">
                      {call.totalPolicies}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Répartition par produit */}
          {call.metadata?.byProduct && Object.keys(call.metadata.byProduct).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Répartition par Produit</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(call.metadata.byProduct).map(([product, data]) => (
                    <div key={product} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{product}</p>
                        <p className="text-sm text-gray-600">
                          {data.count} {data.count > 1 ? 'cotisations' : 'cotisation'}
                        </p>
                      </div>
                      <p className="text-lg font-bold text-primary-600">
                        {formatCurrency(data.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informations de Création</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Créé le</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(call.createdAt)}
                  </p>
                </div>
                {call.sentAt && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Envoyé le</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(call.sentAt)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Button
                  onClick={() => handleDownload('excel')}
                  variant="outline"
                  className="w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger Excel
                </Button>
                <Button
                  onClick={() => handleDownload('pdf')}
                  variant="outline"
                  className="w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger PDF
                </Button>
              </div>

              {call.status === 'DRAFT' && (
                <Button
                  variant="default"
                  className="w-full mt-3"
                  disabled
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Envoyer par Email (Bientôt disponible)
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pied de page */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallDetail;
